import { useMemo } from "react";
import { Check } from "lucide-react";
import type { PermissionCatalogEntry } from "@/lib/staffApi";

const MODULE_LABELS: Record<string, string> = {
  PEDIDOS: "Pedidos / Operações",
  CLIENTES: "Clientes",
  CATALOGO: "Catálogo",
  PRECOS: "Preços",
  SUPRIMENTOS: "Suprimentos",
  ENTREGAS: "Entregas",
  ETIQUETAS: "Etiquetas",
  RELATORIOS: "Relatórios",
  FINANCEIRO: "Financeiro",
  STAFF: "Funcionários e Acessos",
  SELECAO: "Seleção",
  FROTA: "Frota",
};

function humanize(token: string): string {
  const s = token.replace(/_/g, " ").toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

interface Props {
  catalog: PermissionCatalogEntry[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  /** Quando false, permissões protegidas (STAFF.*) ficam desabilitadas (só A/ADM podem recebê-las). */
  targetIsFullAdmin: boolean;
  disabled?: boolean;
}

/**
 * Seleção de acessos por Módulo → Submódulo → Ação. Aplica as dependências no cliente (marcar uma ação
 * marca o Visualizar do submódulo; desmarcar o Visualizar desmarca os dependentes) — o backend revalida.
 */
export function PermissionsEditor({ catalog, selected, onChange, targetIsFullAdmin, disabled }: Props) {
  const byCode = useMemo(() => {
    const m = new Map<string, PermissionCatalogEntry>();
    catalog.forEach((e) => m.set(e.code, e));
    return m;
  }, [catalog]);

  // module -> submodule -> entries
  const grouped = useMemo(() => {
    const mods = new Map<string, Map<string, PermissionCatalogEntry[]>>();
    for (const e of catalog) {
      if (!mods.has(e.module)) mods.set(e.module, new Map());
      const subs = mods.get(e.module)!;
      if (!subs.has(e.submodule)) subs.set(e.submodule, []);
      subs.get(e.submodule)!.push(e);
    }
    return mods;
  }, [catalog]);

  function withDependency(code: string, set: Set<string>) {
    let cur: string | undefined = code;
    const guard = new Set<string>();
    while (cur && !guard.has(cur)) {
      guard.add(cur);
      set.add(cur);
      cur = byCode.get(cur)?.dependsOn ?? undefined;
    }
  }

  function removeWithDependents(code: string, set: Set<string>) {
    set.delete(code);
    // Remove quem depende (direta ou transitivamente) do código removido.
    let changed = true;
    while (changed) {
      changed = false;
      for (const c of Array.from(set)) {
        const dep = byCode.get(c)?.dependsOn;
        if (dep && !set.has(dep)) {
          set.delete(c);
          changed = true;
        }
      }
    }
  }

  function toggle(code: string, checked: boolean) {
    const next = new Set(selected);
    if (checked) withDependency(code, next);
    else removeWithDependents(code, next);
    onChange(next);
  }

  function toggleSubmodule(entries: PermissionCatalogEntry[], checkAll: boolean) {
    const next = new Set(selected);
    if (checkAll) {
      for (const e of entries) {
        if (e.restricted && !targetIsFullAdmin) continue;
        withDependency(e.code, next);
      }
    } else {
      for (const e of entries) removeWithDependents(e.code, next);
    }
    onChange(next);
  }

  return (
    <div className="space-y-3">
      {Array.from(grouped.entries()).map(([module, subs]) => (
        <div key={module} className="rounded-lg border border-border">
          <div className="border-b border-border bg-secondary/40 px-3 py-2 text-xs font-semibold uppercase tracking-wide">
            {MODULE_LABELS[module] ?? module}
          </div>
          <div className="divide-y divide-border/60">
            {Array.from(subs.entries()).map(([submodule, entries]) => {
              const selectable = entries.filter((e) => !(e.restricted && !targetIsFullAdmin));
              const allOn = selectable.length > 0 && selectable.every((e) => selected.has(e.code));
              return (
                <div key={submodule} className="px-3 py-2">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-sm font-medium">{humanize(submodule)}</span>
                    <button
                      type="button"
                      disabled={disabled || selectable.length === 0}
                      className="text-[11px] text-primary underline underline-offset-2 disabled:opacity-40"
                      onClick={() => toggleSubmodule(entries, !allOn)}
                    >
                      {allOn ? "Limpar" : "Selecionar todos"}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                    {entries.map((e) => {
                      const blocked = e.restricted && !targetIsFullAdmin;
                      const on = selected.has(e.code);
                      return (
                        <label
                          key={e.code}
                          className={`flex items-center gap-1.5 text-sm ${blocked ? "opacity-40" : "cursor-pointer"}`}
                          title={blocked ? "Só ADMIN/ADMINISTRATIVO podem receber este acesso" : e.code}
                        >
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5"
                            disabled={disabled || blocked}
                            checked={on}
                            onChange={(ev) => toggle(e.code, ev.target.checked)}
                          />
                          <span>{humanize(e.action)}</span>
                          {e.valueBearing && (
                            <span className="rounded bg-amber-500/10 px-1 text-[10px] text-amber-700" title="Exibe valores">
                              valores
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
        <Check className="h-3 w-3" /> Marcar uma ação marca automaticamente o “Visualizar” exigido; o backend revalida.
      </p>
    </div>
  );
}
